import React from 'react';
import { Pencil } from 'lucide-react';
import type { FieldErrors } from './readinessValidation';

export const inputClass = 'mt-1 min-h-[44px] w-full rounded-md border border-slate-300 px-3 text-sm';
export const labelClass = 'block text-xs font-medium text-slate-600';

/** Inline error under a field */
export const FieldError: React.FC<{ errors: FieldErrors; field: string }> = ({ errors, field }) =>
  errors[field] ? <span role="alert" className="mt-1 block text-xs font-normal text-red-700" data-testid={`error-${field}`}>{errors[field]}</span> : null;

/** Read-only summary row */
export const SummaryRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-0.5 text-sm">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-right text-slate-800">{value === undefined || value === null || value === '' ? '—' : value}</dd>
  </div>
);

export interface ReadinessCardProps {
  title: string;
  icon: React.ReactNode;
  /** Short state shown beside the title (e.g. "Not recorded") */
  status?: React.ReactNode;
  editing: boolean;
  canEdit: boolean;
  saving: boolean;
  errors: FieldErrors;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  summary: React.ReactNode;
  form: React.ReactNode;
}

/** Card shell for one readiness record: a summary, or the inline form with Save / Cancel */
const ReadinessCard: React.FC<ReadinessCardProps> = ({ title, icon, status, editing, canEdit, saving, errors, onEdit, onCancel, onSave, summary, form }) => (
  <section aria-label={title} className="rounded-lg border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
      <h4 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-800">
        <span className="text-slate-500">{icon}</span>
        <span className="truncate">{title}</span>
        {status}
      </h4>
      {canEdit && !editing && (
        <button type="button" onClick={onEdit} className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-md px-2 text-sm text-slate-600 hover:bg-slate-100" aria-label={`Edit ${title}`}>
          <Pencil size={14} />Edit
        </button>
      )}
    </div>
    <div className="px-3 py-2">
      {editing ? (
        <form
          className="space-y-3"
          noValidate
          onSubmit={(e) => { e.preventDefault(); onSave(); }}
        >
          {form}
          {errors._form && <p role="alert" className="text-sm text-red-700">{errors._form}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="min-h-[44px] rounded-md bg-slate-800 px-4 text-sm font-medium text-white disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onCancel} disabled={saving} className="min-h-[44px] rounded-md border border-slate-300 px-4 text-sm text-slate-700">Cancel</button>
          </div>
        </form>
      ) : (
        <dl>{summary}</dl>
      )}
    </div>
  </section>
);

export default ReadinessCard;
